import { useMemo } from "react";
import { IntegrationCoaMapping } from "@common/schemas/domains/accounting/integrationCoaMapping.schema";
import { Task } from "@common/types/task";
import { ViewDensity } from "@common/types/views/views";
import { pluralize } from "@common/helpers/string";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import {
    useIntegrationCoaMappings,
    useUpdateIntegrationCoaMapping,
} from "@api/integrationCoaMapping.api";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewConfig } from "@lib/views";
import { useAccounts } from "@hooks/useAccounts";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { TaskBodyCommandIsland } from "../TaskBodyCommandIsland";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import styles from "./IntegrationCoaMappingTaskBody.module.scss";
import { TransactionTypeCell } from "./cells/TransactionTypeCell";
import { MappedAccountCell } from "./cells/MappedAccountCell";
import type { IntegrationCoaMappingTableMeta } from "./integrationCoaMappingTableMeta";
import {
    countUnmappedMappings,
    getIntegrationCoaMappingTaskScope,
} from "./integrationCoaMappingTask";

interface IntegrationCoaMappingTaskBodyProps {
    selectedTask: Task;
}

enum IntegrationCoaMappingColumn {
    TRANSACTION_TYPE = "transactionType",
    ACCOUNT = "account",
}

const viewConfig: ViewConfig<
    IntegrationCoaMapping,
    IntegrationCoaMappingColumn,
    never
> = {
    columns: {
        [IntegrationCoaMappingColumn.TRANSACTION_TYPE]: {
            accessorKey: "label",
            name: "Transaction type",
            header: "Transaction type",
            immutable: true,
            minSize: 200,
            maxSize: 1200,
            cell: TransactionTypeCell,
        },
        [IntegrationCoaMappingColumn.ACCOUNT]: {
            accessorKey: "accountId",
            name: "Account",
            header: "Account",
            immutable: true,
            minSize: 240,
            maxSize: 1200,
            cell: MappedAccountCell,
        },
    },
};

const view = {
    filters: {},
    columns: [
        { id: IntegrationCoaMappingColumn.TRANSACTION_TYPE },
        { id: IntegrationCoaMappingColumn.ACCOUNT },
    ],
    options: {
        density: ViewDensity.MD,
    },
};

export const IntegrationCoaMappingTaskBody = ({
    selectedTask,
}: IntegrationCoaMappingTaskBodyProps) => {
    const { entityId, provider } =
        getIntegrationCoaMappingTaskScope(selectedTask);

    const {
        data: mappings,
        isLoading,
        isError,
    } = useIntegrationCoaMappings({
        entityId,
        provider,
    });
    const { accountsById } = useAccounts({ entityId, includeDisabled: true });
    const { mutate: updateMapping } = useUpdateIntegrationCoaMapping({
        entityId,
        provider,
    });
    const canEditMappings = useCanPerformWorkspaceAction(
        WorkspaceAction.COA_EDIT,
    );

    const tableMeta: IntegrationCoaMappingTableMeta | undefined = useMemo(
        () =>
            entityId === undefined
                ? undefined
                : {
                      options: { entityId, accountsById, canEditMappings },
                      callbacks: {
                          onSelectAccount: ({ mappingId, accountId }) => {
                              updateMapping({ mappingId, accountId });
                          },
                      },
                  },
        [entityId, accountsById, canEditMappings, updateMapping],
    );

    if (entityId === undefined || provider === undefined || isLoading) {
        return null;
    }

    if (isError) {
        return (
            <EmptyState
                header="Could not load transaction type mappings"
                body="Try again in a moment."
            />
        );
    }

    const rows = mappings ?? [];
    const unmappedCount = countUnmappedMappings(rows);
    const subtitle =
        unmappedCount === 0
            ? `All ${rows.length} ${pluralize("transaction type", rows.length)} are mapped`
            : `${unmappedCount} ${pluralize("transaction type", unmappedCount)} without an account`;

    return (
        <div className={styles.container}>
            {rows.length > 0 && (
                <div className={styles.subtitle}>{subtitle}</div>
            )}

            <ViewTable
                data={rows}
                view={view}
                config={viewConfig}
                className={taskBodyTableStyles.viewTableWithoutTopBorder}
                rowBorders
                columnBorders
                enableColumnResizing={false}
                tableMeta={tableMeta}
                emptyView={() => (
                    <span>
                        No transaction types imported yet. Types appear here
                        after the next sync.
                    </span>
                )}
                footer={<TaskBodyCommandIsland selectedTask={selectedTask} />}
            />
        </div>
    );
};
